import { useEffect, useState } from "react";
import api from "../../api/api";
import "./TransactionDetailsModal.css";

const formatCurrency = (amount, currency) => {
  if (!amount) return "N/A";
  const numAmount = parseFloat(amount) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(numAmount);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-US");
};

const StatusBadge = ({ status }) => {
  if (!status) return null;

  const statusClass =
    status === "success" ? "status-success" : "status-failed";

  return (
    <span className={`status-badge ${statusClass}`}>
      {status.toUpperCase()}
    </span>
  );
};

const TransactionDetailsModal = ({
  showModal,
  setShowModal,
  transactionId,
}) => {
  const [transactionDetails, setTransactionDetails] = useState(null);

  useEffect(() => {
    if (!showModal || !transactionId) return;

    const fetchData = async () => {
      try {
        setTransactionDetails(null);

        const trxRes = await api.get(
          `/api/checkout/v1/transactions/${transactionId}/details`
        );

        setTransactionDetails(trxRes.data);
      } catch (err) {
        console.error("Failed to fetch transaction details", err);
      }
    };

    fetchData();
  }, [showModal, transactionId]);

  if (!showModal) return null;

  const { transaction, customer, invoice, subscription } =
    transactionDetails || {};

  return (
    <div className="modal-overlay" onClick={() => setShowModal(false)}>
      <div className="transaction-details" onClick={(e) => e.stopPropagation()}>
        {!transactionDetails ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading transaction details...</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="details-header">
              <h2>Transaction Details</h2>
              <StatusBadge status={transaction?.status} />
            </div>

            {/* Transaction Section */}
            <div className="details-section">
              <h3>Transaction</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Transaction ID</span>
                  <span className="detail-value mono">
                    {transaction?.id || "N/A"}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Gateway Txn ID</span>
                  <span className="detail-value mono">
                    {transaction?.idAtGateway || "N/A"}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">
                    {transaction?.status || "N/A"}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Gateway</span>
                  <span className="detail-value">
                    {transaction?.gateway || "N/A"}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Type</span>
                  <span className="detail-value">
                    {transaction?.type || "N/A"}
                  </span>
                </div>

                <div className="detail-item full-width">
                  <span className="detail-label">Amount</span>
                  <span className="detail-value amount">
                    {formatCurrency(
                      transaction?.amount,
                      transaction?.currency
                    )}
                  </span>
                </div>

                <div className="detail-item full-width">
                  <span className="detail-label">Created At</span>
                  <span className="detail-value">
                    {formatDate(transaction?.createdAt)}
                  </span>
                </div>

                {transaction?.errorMessage && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Error</span>
                    <span className="detail-value error">
                      {transaction.errorMessage}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Section */}
            {customer && (
              <div className="details-section">
                <h3>Customer</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Customer ID</span>
                    <span className="detail-value mono">
                      {customer?.idAtBillingPlatform || "N/A"}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Platform</span>
                    <span className="detail-value">
                      {customer?.billingPlatform || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Invoice Section */}
            {invoice && (
              <div className="details-section">
                <h3>Invoice</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Invoice ID</span>
                    <span className="detail-value mono">
                      {invoice?.idAtBillingPlatform || "N/A"}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Platform</span>
                    <span className="detail-value">
                      {invoice?.billingPlatform || "N/A"}
                    </span>
                  </div>

                  <div className="detail-item full-width">
                    <span className="detail-label">Amount</span>
                    <span className="detail-value amount">
                      {formatCurrency(invoice?.amount, invoice?.currency)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Section */}
            {subscription && (
              <div className="details-section">
                <h3>Subscription</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Subscription ID</span>
                    <span className="detail-value mono">
                      {subscription?.idAtBillingPlatform || "N/A"}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Platform</span>
                    <span className="detail-value">
                      {subscription?.billingPlatform || "N/A"}
                    </span>
                  </div>

                  <div className="detail-item full-width">
                    <span className="detail-label">Next Billing Date</span>
                    <span className="detail-value">
                      {formatDate(subscription?.nextBillingDate)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="modal-actions">
              <button
                className="close-button"
                onClick={() => setShowModal(false)}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionDetailsModal;